import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Bell,
  BellOff,
  BookOpen,
  CheckCircle2,
  CircleHelp,
  Heart,
  History,
  Lightbulb,
  MapPin,
  Menu,
  MessageSquareText,
  Scale,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Button from "../components/Button";
import AccentRays from "../components/decor/AccentRays";
import DashboardSidebar from "../components/dashboard/DashboardSidebar";
import DashboardEmptyState from "../components/dashboard/DashboardEmptyState";
import { useCompare } from "../context/CompareContext";
import { useLikes } from "../context/LikesContext";
import { schools } from "../data/schools";
import { formatRupiahShort } from "../lib/format";
import brandLogo from "../assets/images/MySchoolMate.png";
import SEO from "../components/SEO";

const PROFILE_KEY = "msm_parent_profile";
const emptyProfile = {
  parentName: "",
  childName: "",
  level: "",
  city: "",
  budget: "",
  curriculum: "",
  interests: "",
};

function readProfile() {
  try {
    const stored = localStorage.getItem(PROFILE_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed && typeof parsed === "object" ? { ...emptyProfile, ...parsed } : emptyProfile;
  } catch {
    return emptyProfile;
  }
}

function StatCard({ icon: Icon, label, value, helper, accent, to }) {
  return (
    <article className="rounded-[20px] border border-ink/10 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-[13px] ${accent}`}>
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        {to ? (
          <Link to={to} className="rounded-md text-xs font-bold text-purple hover:underline focus-ring">
            Lihat
          </Link>
        ) : null}
      </div>
      <p className="mt-4 text-xs font-bold uppercase text-ink/45">{label}</p>
      <p className="mt-1 text-2xl font-extrabold text-ink">{value}</p>
      <p className="mt-1 text-xs font-medium text-ink/50">{helper}</p>
    </article>
  );
}

function DashboardSchoolCard({ school }) {
  const { isLiked, toggleLike } = useLikes();
  const liked = isLiked(school.slug);

  return (
    <article className="flex min-w-[276px] flex-col overflow-hidden rounded-[20px] border border-ink/10 bg-white shadow-sm md:min-w-0">
      <div className="relative aspect-[16/9] overflow-hidden bg-cream">
        <Link to={`/schools/${school.slug}`}>
          <img src={school.image} alt={school.name} loading="lazy" className="h-full w-full object-cover transition duration-300 hover:scale-[1.03]" />
        </Link>
        <button
          type="button"
          onClick={() => toggleLike(school.slug)}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-white text-ink shadow-sm hover:bg-cream focus-ring"
          aria-label={liked ? `Hapus ${school.name} dari sekolah disukai` : `Sukai ${school.name}`}
        >
          <Heart className={`h-[18px] w-[18px] ${liked ? "fill-coral text-coral" : "text-ink"}`} aria-hidden="true" />
        </button>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-lg font-extrabold leading-snug text-ink">{school.name}</h3>
        <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-ink/55">
          <MapPin className="h-4 w-4 text-mint" aria-hidden="true" />
          {school.city}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold">
          <span className="rounded-lg bg-purple/10 px-2.5 py-1.5 text-purple">{school.curriculum}</span>
          <span className="rounded-lg bg-[#FFF3D7] px-2.5 py-1.5 text-ink">{formatRupiahShort(school.monthlyTuition)}/bulan</span>
        </div>
        <Link to={`/schools/${school.slug}`} className="mt-4 inline-flex items-center gap-1 text-sm font-extrabold text-ink hover:text-purple focus-ring">
          Lihat Sekolah
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

function SectionHeader({ title, description, action }) {
  return (
    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
      <div>
        <h2 className="text-xl font-extrabold text-ink sm:text-2xl">{title}</h2>
        {description ? <p className="mt-1 text-sm font-medium text-ink/55">{description}</p> : null}
      </div>
      {action ? (
        <Link to={action.to} className="inline-flex w-fit items-center gap-1 text-sm font-extrabold text-purple hover:underline focus-ring">
          {action.label}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}

function Overview({ displayName, likedCount, compareCount }) {
  const recommended = schools.slice(0, 3);

  return (
    <>
      <div className="relative">
        <AccentRays variant="curved-rays" color="yellow" rotation={12} className="absolute left-44 top-0 hidden h-9 w-9 sm:block" />
        <p className="text-sm font-bold text-[#B46F00]">Overview keluarga</p>
        <h1 className="mt-1 text-3xl font-extrabold text-ink sm:text-4xl">Halo, {displayName}!</h1>
        <p className="mt-2 max-w-2xl font-medium text-ink/60">
          Lanjutkan perjalanan menemukan sekolah yang sesuai untuk anak Anda.
        </p>
      </div>

      <section className="mt-7 grid grid-cols-2 gap-3 xl:grid-cols-4 xl:gap-4" aria-label="Ringkasan dashboard">
        <StatCard icon={Sparkles} label="AI Match Terakhir" value="Belum ada" helper="Belum ada riwayat tersimpan" accent="bg-[#FFF3D7] text-[#B46F00]" to="/dashboard/orang-tua/ai-match" />
        <StatCard icon={Heart} label="Sekolah Disukai" value={likedCount} helper="Tersimpan di perangkat ini" accent="bg-mint/20 text-ink" to="/dashboard/orang-tua/likes" />
        <StatCard icon={MessageSquareText} label="Review Saya" value={0} helper="Belum ada review" accent="bg-purple/10 text-purple" to="/dashboard/orang-tua/reviews" />
        <StatCard icon={Scale} label="Perbandingan Aktif" value={compareCount} helper="Maksimal 5 sekolah" accent="bg-coral/15 text-coral" to="/compare" />
      </section>

      <section className="mt-9">
        <SectionHeader
          title="Rekomendasi untuk Anda"
          description="Jelajahi beberapa pilihan sekolah yang tersedia di MySchoolMate."
          action={{ label: "Lihat semua", to: "/schools" }}
        />
        <div className="-mx-1 mt-5 flex gap-4 overflow-x-auto px-1 pb-3 md:grid md:grid-cols-3 md:overflow-visible md:pb-0">
          {recommended.map((school) => <DashboardSchoolCard key={school.slug} school={school} />)}
        </div>
      </section>

      <section className="mt-7 rounded-[22px] border border-[#F7AB2D]/30 bg-[#FFF9EA] p-5 sm:flex sm:items-center sm:justify-between sm:gap-6 sm:p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-[#F7AB2D] text-ink">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-extrabold text-ink">Belum mencoba AI School Match?</h2>
            <p className="mt-1 text-sm font-medium leading-relaxed text-ink/60">
              Ceritakan kebutuhan keluarga untuk mendapatkan pilihan yang lebih terarah.
            </p>
          </div>
        </div>
        <Button to="/match" variant="yellow" className="mt-4 min-h-11 w-full shrink-0 sm:mt-0 sm:w-auto">
          Mulai AI School Match
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </section>

      <div className="mt-7 grid gap-5 xl:grid-cols-[1.5fr_0.8fr]">
        <section className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-6">
          <SectionHeader title="Aktivitas Terakhir" />
          <div className="mt-5 flex min-h-[190px] flex-col items-center justify-center rounded-[16px] bg-warm px-5 text-center">
            <History className="h-8 w-8 text-ink/25" aria-hidden="true" />
            <p className="mt-3 font-extrabold text-ink">Belum ada aktivitas.</p>
            <p className="mt-1 max-w-md text-sm font-medium text-ink/50">Mulai menjelajahi sekolah atau coba AI School Match.</p>
          </div>
        </section>

        <aside className="rounded-[22px] border border-[#F7AB2D]/30 bg-[#FFF3D7] p-5 sm:p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-white text-[#B46F00]">
            <Lightbulb className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-xl font-extrabold text-ink">Tips untuk Orang Tua</h2>
          <p className="mt-2 text-sm font-medium leading-relaxed text-ink/65">
            Bandingkan beberapa sekolah untuk melihat perbedaan biaya, kurikulum, fasilitas, dan program dengan lebih mudah.
          </p>
          <Button to="/compare" variant="secondary" className="mt-5 min-h-11 w-full bg-white/70">
            <Scale className="h-4 w-4" aria-hidden="true" />
            Mulai Membandingkan
          </Button>
        </aside>
      </div>
    </>
  );
}

function LikesPage({ likedSchools }) {
  if (!likedSchools.length) {
    return <DashboardEmptyState icon={Heart} title="Belum ada sekolah yang disukai." description="Simpan sekolah yang menarik agar mudah ditemukan kembali di sini." action={{ label: "Jelajahi Sekolah", to: "/schools", icon: ArrowRight }} />;
  }
  return (
    <div>
      <SectionHeader title="Sekolah Disukai" description={`${likedSchools.length} sekolah tersimpan di perangkat ini.`} />
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {likedSchools.map((school) => <DashboardSchoolCard key={school.slug} school={school} />)}
      </div>
    </div>
  );
}

function ProfilePage({ profile, setProfile }) {
  const [draft, setDraft] = useState(profile);
  const [saved, setSaved] = useState(false);
  const fields = [
    ["parentName", "Nama Orang Tua / Wali", "Masukkan nama"],
    ["childName", "Nama / Panggilan Anak", "Masukkan nama atau panggilan"],
    ["level", "Jenjang yang Dicari", "Contoh: SD"],
    ["city", "Kota", "Contoh: Bandung"],
    ["budget", "Budget Bulanan", "Contoh: Rp2.000.000"],
    ["curriculum", "Kurikulum", "Contoh: Kurikulum Merdeka"],
  ];
  const filled = Object.values(draft).filter((value) => String(value).trim()).length;

  const saveProfile = (event) => {
    event.preventDefault();
    setProfile(draft);
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(draft));
    } catch {
      /* Keep the in-memory value when storage is unavailable. */
    }
    setSaved(true);
  };

  return (
    <div>
      <SectionHeader title="Profil Keluarga" description="Simpan preferensi dasar keluarga di perangkat ini." />
      <div className="mt-6 grid gap-5 xl:grid-cols-[1fr_300px]">
        <form onSubmit={saveProfile} className="rounded-[22px] border border-ink/10 bg-white p-5 shadow-sm sm:p-7">
          <div className="grid gap-5 sm:grid-cols-2">
            {fields.map(([name, label, placeholder]) => (
              <label key={name} className="block">
                <span className="mb-2 block text-sm font-bold text-ink">{label}</span>
                <input
                  value={draft[name]}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, [name]: event.target.value }));
                    setSaved(false);
                  }}
                  placeholder={placeholder}
                  className="min-h-12 w-full rounded-xl border border-ink/15 px-4 text-sm font-medium text-ink outline-none focus:border-purple focus:ring-4 focus:ring-purple/10"
                />
              </label>
            ))}
            <label className="block sm:col-span-2">
              <span className="mb-2 block text-sm font-bold text-ink">Minat Anak</span>
              <textarea
                value={draft.interests}
                onChange={(event) => {
                  setDraft((current) => ({ ...current, interests: event.target.value }));
                  setSaved(false);
                }}
                placeholder="Contoh: Coding, olahraga, atau seni"
                className="min-h-[100px] w-full resize-y rounded-xl border border-ink/15 p-4 text-sm font-medium text-ink outline-none focus:border-purple focus:ring-4 focus:ring-purple/10"
              />
            </label>
          </div>
          <Button type="submit" variant="primary" className="mt-6 min-h-11 w-full sm:w-auto">
            Simpan Profil
          </Button>
          {saved ? <p className="mt-3 flex items-center gap-2 text-sm font-bold text-ink"><CheckCircle2 className="h-4 w-4 text-mint" />Profil tersimpan di perangkat ini.</p> : null}
        </form>
        <aside className="rounded-[22px] border border-[#F7AB2D]/25 bg-[#FFF9EA] p-6">
          <UsersRound className="h-7 w-7 text-[#B46F00]" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-extrabold text-ink">Kelengkapan profil</h2>
          <p className="mt-2 text-sm font-medium text-ink/60">{filled} dari {Object.keys(emptyProfile).length} informasi terisi.</p>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-white">
            <div className="h-full rounded-full bg-[#F7AB2D]" style={{ width: `${(filled / Object.keys(emptyProfile).length) * 100}%` }} />
          </div>
        </aside>
      </div>
    </div>
  );
}

function SettingsPage() {
  const [communication, setCommunication] = useState(false);
  return (
    <div>
      <SectionHeader title="Pengaturan" description="Pengaturan lokal untuk pengalaman dashboard." />
      <section className="mt-6 rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm">
        <h2 className="font-extrabold text-ink">Preferensi komunikasi</h2>
        <label className="mt-4 flex items-start gap-3 rounded-[16px] bg-warm p-4">
          <input type="checkbox" checked={communication} onChange={(event) => setCommunication(event.target.checked)} className="mt-1 h-4 w-4 accent-[#F7AB2D]" />
          <span className="text-sm font-medium text-ink/65">Tandai preferensi untuk menerima pembaruan ketika layanan komunikasi tersedia.</span>
        </label>
        <div className="mt-5 border-t border-ink/10 pt-5">
          <h2 className="font-extrabold text-ink">Password dan keamanan</h2>
          <p className="mt-2 text-sm font-medium text-ink/55">Perubahan akun belum tersedia karena autentikasi belum terhubung ke backend.</p>
        </div>
      </section>
    </div>
  );
}

function HelpPage() {
  return (
    <div>
      <SectionHeader title="Bantuan" description="Temukan jalur yang tepat untuk melanjutkan penggunaan MySchoolMate." />
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Link to="/tentang" className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 focus-ring">
          <CircleHelp className="h-7 w-7 text-[#B46F00]" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-extrabold text-ink">Tentang dan bantuan</h2>
          <p className="mt-2 text-sm font-medium text-ink/55">Pelajari tujuan MySchoolMate dan informasi bantuan yang tersedia.</p>
        </Link>
        <Link to="/schools" className="rounded-[22px] border border-ink/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 focus-ring">
          <BookOpen className="h-7 w-7 text-mint" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-extrabold text-ink">Jelajahi sekolah</h2>
          <p className="mt-2 text-sm font-medium text-ink/55">Kembali ke katalog untuk melihat pilihan sekolah yang tersedia.</p>
        </Link>
      </div>
    </div>
  );
}

export default function ParentDashboard() {
  const { pathname } = useLocation();
  const { count: compareCount } = useCompare();
  const { slugs: likedSlugs, count: likedCount } = useLikes();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(readProfile);
  const displayName = profile.parentName.trim() || "Pengguna";
  const likedSchools = useMemo(() => schools.filter((school) => likedSlugs.includes(school.slug)), [likedSlugs]);

  let content;
  if (pathname.endsWith("/ai-match")) {
    content = <DashboardEmptyState icon={Sparkles} title="Belum ada riwayat AI Match." description="Setelah Anda menggunakan AI School Match, hasil pencarian sebelumnya akan muncul di sini." action={{ label: "Mulai AI School Match", to: "/match", icon: ArrowRight }} />;
  } else if (pathname.endsWith("/likes")) {
    content = <LikesPage likedSchools={likedSchools} />;
  } else if (pathname.endsWith("/reviews")) {
    content = <DashboardEmptyState icon={MessageSquareText} title="Belum ada review yang Anda tulis." description="Review yang Anda buat nantinya akan tersusun di halaman ini." action={{ label: "Jelajahi Sekolah", to: "/schools", icon: ArrowRight }} />;
  } else if (pathname.endsWith("/profil")) {
    content = <ProfilePage profile={profile} setProfile={setProfile} />;
  } else if (pathname.endsWith("/notifikasi")) {
    content = <DashboardEmptyState icon={BellOff} title="Belum ada notifikasi baru." description="Pembaruan akun dan aktivitas akan tampil di sini ketika sistem notifikasi tersedia." />;
  } else if (pathname.endsWith("/pengaturan")) {
    content = <SettingsPage />;
  } else if (pathname.endsWith("/bantuan")) {
    content = <HelpPage />;
  } else {
    content = <Overview displayName={displayName} likedCount={likedCount} compareCount={compareCount} />;
  }

  return (
    <div className="min-h-screen bg-warm lg:flex">
      <SEO title="Dashboard Orang Tua | MySchoolMate" canonicalPath="/dashboard/orang-tua" noIndex />
      <DashboardSidebar pathname={pathname} open={sidebarOpen} onClose={() => setSidebarOpen(false)} displayName={displayName} />

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-ink/10 bg-white/95 px-4 backdrop-blur lg:hidden">
          <Link to="/" className="rounded-lg focus-ring" aria-label="Kembali ke beranda MySchoolMate">
            <img src={brandLogo} alt="MySchoolMate" width="288" height="43" className="h-7 w-auto" />
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-ink/15 bg-white text-ink focus-ring"
            aria-label="Buka menu dashboard"
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <main className="relative mx-auto max-w-[1380px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8 xl:px-10 xl:py-10">
          <div className="pointer-events-none absolute right-8 top-8 hidden opacity-25 xl:block" aria-hidden="true">
            <Bell className="h-5 w-5 text-purple" />
          </div>
          {content}
        </main>
      </div>
    </div>
  );
}
